/**
 * SelectOptionsEditor — F12.A2 T12.A2.1.
 *
 * Edytor opcji dla pól typu select/multiselect/radio/checkboxgroup.
 * Adaptacja UI 1:1 z SellCall (`ModuleBuilderPage.tsx:3074` SelectOptionsEditor).
 * Backend: bulk replace przez PUT `/api/modules/:id/fields/:fieldId/options`.
 *
 * Renderowany w prawym panelu builder gdy field.fieldType in ['select','multiselect','radio','checkboxgroup'].
 *
 * NEG scope T12.A2.1:
 *   - Dictionary mode (slownik globalny) — deferred do F12.B (gdy mamy CRUD słowników w settings)
 *   - Per-option icon — deferred (SellCall ma, my MVP)
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronUp, Edit2, List, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const PRESET_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316',
  '#6366F1',
  '#14B8A6',
];

export interface FieldOption {
  id?: string;
  value: string;
  label: string;
  color?: string | null;
  sortOrder?: number;
}

interface SelectOptionsEditorProps {
  moduleId: string;
  fieldId: string;
  fieldSlug: string;
  initialOptions: FieldOption[];
}

/** Konwertuje label na slug (replace spaces → _, strip non-alnum, zachowuje polskie znaki). */
function labelToValue(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_ąćęłńóśźż]/g, '')
      .replace(/^_|_$/g, '') || 'opt'
  );
}

export function SelectOptionsEditor({
  moduleId,
  fieldId,
  fieldSlug,
  initialOptions,
}: SelectOptionsEditorProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [options, setOptions] = useState<FieldOption[]>(initialOptions);
  const [newLabel, setNewLabel] = useState('');
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  // Reset gdy field się zmienia (parent przekazuje inne initialOptions)
  useEffect(() => {
    setOptions(initialOptions);
    setEditingIdx(null);
  }, [fieldId, initialOptions]);

  const hasChanges = useMemo(() => {
    if (options.length !== initialOptions.length) return true;
    return options.some((o, i) => {
      const orig = initialOptions[i];
      if (!orig) return true;
      return o.value !== orig.value || o.label !== orig.label || o.color !== orig.color;
    });
  }, [options, initialOptions]);

  const saveMutation = useMutation({
    mutationFn: async (opts: FieldOption[]) => {
      const payload = opts.map((o, idx) => ({
        value: o.value,
        label: o.label,
        color: o.color ?? null,
        sortOrder: idx,
      }));
      const res = await api.put(`/modules/${moduleId}/fields/${fieldId}/options`, {
        options: payload,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.options.toasts.saved') });
      queryClient.invalidateQueries({ queryKey: ['modules', moduleId] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.options.toasts.saveError') });
    },
  });

  const addOption = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    const value = labelToValue(trimmed);
    // Jeśli value już istnieje — dorzuć sufiks
    let finalValue = value;
    let suffix = 2;
    while (options.some((o) => o.value === finalValue)) {
      finalValue = `${value}_${suffix++}`;
    }
    const color = PRESET_COLORS[options.length % PRESET_COLORS.length];
    setOptions([...options, { label: trimmed, value: finalValue, color: color ?? null }]);
    setNewLabel('');
  };

  const removeOption = (idx: number) => {
    setOptions(options.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(null);
  };

  const updateOption = (idx: number, updates: Partial<FieldOption>) => {
    setOptions(options.map((o, i) => (i === idx ? { ...o, ...updates } : o)));
  };

  const moveOption = (idx: number, direction: 'up' | 'down') => {
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= options.length) return;
    const next = [...options];
    [next[idx], next[newIdx]] = [next[newIdx]!, next[idx]!];
    setOptions(next);
    if (editingIdx === idx) setEditingIdx(newIdx);
    else if (editingIdx === newIdx) setEditingIdx(idx);
  };

  return (
    <div
      className="mt-4 border-t border-border-primary pt-4"
      data-testid={`options-editor-${fieldSlug}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <List className="h-4 w-4" />
          {t('tenant.modules.options.title')}
        </h4>
        {hasChanges && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveMutation.mutate(options)}
            loading={saveMutation.isPending}
            disabled={saveMutation.isPending}
            data-testid={`options-editor-${fieldSlug}-save`}
          >
            {t('tenant.modules.options.save')}
          </Button>
        )}
      </div>

      {/* Custom options list */}
      <div className="mb-3 max-h-64 space-y-1 overflow-y-auto">
        {options.length === 0 && (
          <p className="px-2 py-3 text-center text-xs text-text-tertiary">
            {t('tenant.modules.options.empty')}
          </p>
        )}
        {options.map((opt, idx) => (
          <div
            key={opt.id ?? `new-${idx}`}
            className="group flex items-center gap-1.5 rounded-md border border-transparent p-1.5 transition hover:border-border-primary hover:bg-bg-tertiary"
            data-testid={`options-editor-${fieldSlug}-row-${idx}`}
          >
            <input
              type="color"
              value={opt.color ?? '#6B7280'}
              onChange={(e) => updateOption(idx, { color: e.target.value })}
              className="h-5 w-5 shrink-0 cursor-pointer rounded border-0 p-0"
              title={t('tenant.modules.options.colorPicker')}
            />
            {editingIdx === idx ? (
              <div className="flex flex-1 gap-1">
                <input
                  type="text"
                  value={opt.label}
                  onChange={(e) => updateOption(idx, { label: e.target.value })}
                  className="flex-1 rounded border border-accent-400 bg-bg-primary px-1.5 py-0.5 text-xs text-text-primary"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') {
                      // Esc „obsłużony" — okno, w którym stoi edytor opcji, się nie zamyka.
                      if (e.key === 'Escape') e.preventDefault();
                      setEditingIdx(null);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setEditingIdx(null)}
                  className="text-accent-600 hover:text-accent-700"
                  title={t('tenant.modules.options.done')}
                  aria-label={t('tenant.modules.options.done')}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <>
                <span
                  className="flex-1 cursor-pointer truncate text-xs text-text-secondary"
                  onClick={() => setEditingIdx(idx)}
                  title={`${opt.label} (${opt.value})`}
                >
                  {opt.label}
                </span>
                <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => moveOption(idx, 'up')}
                    disabled={idx === 0}
                    className="p-0.5 text-text-muted hover:text-text-secondary disabled:opacity-30"
                    aria-label={t('tenant.modules.options.moveUp')}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveOption(idx, 'down')}
                    disabled={idx === options.length - 1}
                    className="p-0.5 text-text-muted hover:text-text-secondary disabled:opacity-30"
                    aria-label={t('tenant.modules.options.moveDown')}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingIdx(idx)}
                    className="p-0.5 text-text-muted hover:text-accent-600"
                    aria-label={t('tenant.modules.options.edit')}
                  >
                    <Edit2 className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeOption(idx)}
                    className="p-0.5 text-text-muted hover:text-red-600"
                    aria-label={t('tenant.modules.options.remove')}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add new option */}
      <div className="flex gap-1">
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOption();
            }
          }}
          placeholder={t('tenant.modules.options.newOptionPlaceholder')}
          className="flex-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1.5 text-xs text-text-primary placeholder-text-muted focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
          data-testid={`options-editor-${fieldSlug}-new-label`}
        />
        <button
          type="button"
          onClick={addOption}
          disabled={!newLabel.trim()}
          className="flex items-center gap-1 rounded-md bg-accent-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          data-testid={`options-editor-${fieldSlug}-add`}
        >
          <Plus className="h-3 w-3" />
          {t('tenant.modules.options.add')}
        </button>
      </div>
    </div>
  );
}
